import React from "react";
import { Link } from "react-router-dom";
import { Shield, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";

interface ProfileCardProps {
  profile: {
    completionPercentage: number;
    isFullyVerified: boolean;
    verificationSteps: { name: string; completed: boolean }[];
    pendingSteps: string[];
  };
  loading?: boolean;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-32"></div>
          <div className="h-2 bg-gray-200 rounded w-full"></div>
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded w-24"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Profile Verification</h3>
        </div>
        <span className="text-sm font-medium text-blue-600">
          {profile.completionPercentage}%
        </span>
      </div>

      <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${
            profile.isFullyVerified ? "bg-green-500" : "bg-blue-500"
          }`}
          style={{ width: `${profile.completionPercentage}%` }}
        />
      </div>

      <div className="space-y-2 mb-4">
        {profile.verificationSteps.map((step) => (
          <div key={step.name} className="flex items-center gap-2 text-sm">
            {step.completed ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <AlertCircle className="w-4 h-4 text-gray-300" />
            )}
            <span
              className={step.completed ? "text-gray-700" : "text-gray-400"}
            >
              {step.name}
            </span>
          </div>
        ))}
      </div>

      {!profile.isFullyVerified && (
        <Link
          to="/profile"
          className="flex items-center justify-center gap-2 w-full py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
        >
          Complete Verification
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
};

export default ProfileCard;
